import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Header } from '../../components/Header';
import { useAdmin } from '../../context/useAdmin';
import type { Access } from '../../lib/access';
import { ADMIN_SIMULATED, resetAdminStore } from '../../lib/adminApi';

/**
 * The admin area's pages, in the order somebody setting up a company walks
 * through them: the company, its wells, what each role may do, what each role
 * sees, and finally the people.
 */
const TABS = [
  { key: 'Companies', needs: 'structure' },
  { key: 'Wells', needs: 'structure' },
  { key: 'Roles', needs: 'structure' },
  { key: 'Layouts', needs: 'structure' },
  { key: 'Users', needs: 'users' },
] as const;

export type AdminTab = (typeof TABS)[number]['key'];

const allowedTabs = (access: Access) => TABS.filter((t) => access.admin[t.needs]);

/** /web/Admin: on to the first page this person may open, or out. */
export function AdminIndex() {
  const { access } = useAdmin();
  const first = allowedTabs(access)[0];
  return <Navigate to={first ? `/web/Admin/${first.key}` : '/web'} replace />;
}

interface Props {
  active: AdminTab;
  /** The page's heading: "Well Management". */
  title: string;
  /** What sits in the pill beside it: "2 Wells". */
  count?: ReactNode;
  /** The page's one main button, at the right of the heading. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The frame every admin page sits in. It is the frame of Company Management
 * (pages/CompaniesHome), so moving between the tabs changes the list and
 * nothing around it: heading and count, the main button, the tabs.
 */
export function AdminShell({ active, title, count, action, children }: Props) {
  const { access, commit, previewAs, storeLoaded, wellsError } = useAdmin();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const tabs = allowedTabs(access);

  // The API will refuse anyone else; hiding the page is the courtesy.
  if (!tabs.some((t) => t.key === active)) return <AdminIndex />;

  async function clearAll() {
    setBusy(true);
    try {
      previewAs(null);
      await commit(resetAdminStore());
    } finally {
      setBusy(false);
      setAsking(false);
    }
  }

  return (
    <div className="app">
      <Header context="wells" />

      <main className="cmpage">
        <header className="cmhead">
          <h1>{title}</h1>
          {count != null && <span className="cmcount">{count}</span>}
          <span className="cmspacer" />
          {action}
        </header>

        <nav className="cmtabs" aria-label="Administration">
          {tabs.map((t) =>
            t.key === active ? (
              <span key={t.key} className="cmtab is-active" aria-current="page">
                {t.key}
              </span>
            ) : (
              <Link key={t.key} to={`/web/Admin/${t.key}`} className="cmtab">
                {t.key}
              </Link>
            ),
          )}
        </nav>

        {/* Until the server has answered there is nothing to show but empty
            lists, and an empty list reads as "nothing exists". */}
        {!storeLoaded && <p className="cmempty">Loading…</p>}
        {/* The Wells page says so itself, with what that means for its list. */}
        {storeLoaded && wellsError && active !== 'Wells' && (
          <div className="alert" role="alert">
            {wellsError}
          </div>
        )}
        {storeLoaded && children}

        {ADMIN_SIMULATED && (
          <p className="cmsim">
            Simulated: companies, roles, layouts and users are kept in this browser only, and a user created here
            cannot sign in yet. They move to the server once the API has these endpoints.{' '}
            {access.kind === 'superadmin' &&
              (asking ? (
                <>
                  Throw away everything on these pages?{' '}
                  <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={() => void clearAll()} disabled={busy}>
                    Clear
                  </button>{' '}
                  <button type="button" className="cmbtn cmbtn--small" onClick={() => setAsking(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button type="button" className="adminlink" onClick={() => setAsking(true)}>
                  Clear the simulated data
                </button>
              ))}
          </p>
        )}
      </main>
    </div>
  );
}

/**
 * The error and the confirmation, where every admin page shows them. While a
 * dialog is open the error is shown inside it instead, next to what caused it.
 */
export function ActionMessages({
  error,
  notice,
  inDialog = false,
}: {
  error: string | null;
  notice: string | null;
  inDialog?: boolean;
}) {
  return (
    <>
      {error && !inDialog && (
        <div className="alert cmmsg" role="alert">
          {error}
        </div>
      )}
      {notice && !error && (
        <p className="kbnotice cmmsg" role="status">
          {notice}
        </p>
      )}
    </>
  );
}

/** "Delete" in a row of the list; it asks once before it does. */
export function ConfirmDelete({
  question,
  busy,
  onDelete,
}: {
  question: string;
  busy: boolean;
  onDelete: () => void;
}) {
  const [asking, setAsking] = useState(false);

  if (!asking) {
    return (
      <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={() => setAsking(true)} disabled={busy}>
        Delete
      </button>
    );
  }

  return (
    <>
      <span className="cmconfirm">{question}</span>
      <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={onDelete} disabled={busy}>
        Delete
      </button>
      <button type="button" className="cmbtn cmbtn--small" onClick={() => setAsking(false)}>
        Keep
      </button>
    </>
  );
}
